/**
 * Plaid OAuth resume session storage.
 *
 * Persists the in-flight Plaid Link session across the OAuth full-page
 * redirect so the ACH page can resume the SAME Link session on return.
 *
 * Uses localStorage (NOT sessionStorage): on mobile web Plaid may open the
 * bank OAuth in a new tab, and app2app banks bounce through the bank's native
 * app and can return in a fresh browsing context. sessionStorage is per-tab
 * and would be empty on the returning context; localStorage is origin-scoped
 * and survives both. Plaid's own Pattern sample uses localStorage for the same
 * reason. Single-use cleanup + expiration neutralize the only downside.
 */

const KEY_PREFIX = "alpaca:plaid-oauth:v1:";

// Plaid primary-flow link tokens are valid ~4h; used as a fallback ceiling
// when the stored `expiration` cannot be parsed.
const FALLBACK_TTL_MS = 4 * 60 * 60 * 1000;

export type PlaidOAuthSession = {
  link_token: string;
  /** ISO timestamp from Plaid's /link/token/create response. */
  expiration: string;
  accountID: string;
  /** window.location.search at open time (preserves ?transfer=deposit|withdraw). */
  search: string;
  /** Date.now() when persisted. */
  savedAt: number;
};

const keyFor = (accountID: string) => `${KEY_PREFIX}${accountID}`;

/**
 * Returns true if the session was persisted. A false return means localStorage
 * is unavailable (private mode / quota / ITP) and OAuth resume will not work on
 * this device - callers should emit telemetry so we can quantify these returns,
 * which otherwise surface later as an unexplained "orphaned" OAuth return.
 */
export const savePlaidOAuthSession = (session: PlaidOAuthSession): boolean => {
  try {
    localStorage.setItem(keyFor(session.accountID), JSON.stringify(session));
    return true;
  } catch {
    return false;
  }
};

export const readPlaidOAuthSession = (
  accountID: string,
): PlaidOAuthSession | null => {
  if (!accountID) {
    return null;
  }
  try {
    const raw = localStorage.getItem(keyFor(accountID));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<PlaidOAuthSession>;
    if (
      !parsed ||
      typeof parsed.link_token !== "string" ||
      !parsed.link_token ||
      parsed.accountID !== accountID ||
      typeof parsed.expiration !== "string" ||
      typeof parsed.savedAt !== "number" ||
      typeof parsed.search !== "string"
    ) {
      return null;
    }
    return parsed as PlaidOAuthSession;
  } catch {
    return null;
  }
};

export const clearPlaidOAuthSession = (accountID: string): void => {
  if (!accountID) {
    return;
  }
  try {
    localStorage.removeItem(keyFor(accountID));
  } catch {
    // ignore
  }
};

/**
 * True only when the session exists, belongs to this account, has a token,
 * and has not expired. Type guard so callers can safely read the session.
 */
export const isPlaidOAuthSessionValid = (
  session: PlaidOAuthSession | null,
  accountID: string,
): session is PlaidOAuthSession => {
  if (!session?.link_token || session.accountID !== accountID) {
    return false;
  }
  const expiresAt = Date.parse(session.expiration);
  if (Number.isNaN(expiresAt)) {
    return Date.now() - session.savedAt < FALLBACK_TTL_MS;
  }
  return expiresAt > Date.now();
};
